/**
 * TaskViewPage Component
 * Full page view for displaying task details
 * Handles task viewing with comments, worklog, and history
 */
import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import TaskViewModal from './TaskViewModal';
import EditTaskModal from './EditTaskModal';
import Navbar from '../shared/Navbar';
import { COLORS } from '../../shared/colors';
import { fetchTicketDetails, fetchTicketComments, fetchWorklogSummary, fetchTicketHistory, updateTicket, clearTicketDetails, fetchTicketIdByNumber, fetchProjectDropdownsByUser } from '../../store/ticketSlice';
import { markNotificationsRead } from '../../store/notificationSlice';
import '../users/UserManagement.css';

const TaskViewPage = () => {
  const { t } = useTranslation();
  const { ticket_number, '*': taskExtraPath = '' } = useParams();
  const { search } = useLocation();
  const isFromEmail = new URLSearchParams(search).get('from_email') === 'true';
  const focusedCommentId = React.useMemo(() => {
    if (!taskExtraPath || !taskExtraPath.startsWith('focusedCommentId=')) {
      return '';
    }

    const rawCommentId = taskExtraPath.slice('focusedCommentId='.length).trim();
    if (!rawCommentId) {
      return '';
    }

    try {
      return decodeURIComponent(rawCommentId);
    } catch (error) {
      return rawCommentId;
    }
  }, [taskExtraPath]);
  const dispatch = useDispatch();
  const { ticketDetails, ticketDetailsLoading, ticketIdByNumber, ticketIdByNumberLoading, error } = useSelector(state => state.tickets);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  useEffect(() => {
    if (ticket_number) {
      // Clear previous data first
      dispatch(clearTicketDetails());
      dispatch(fetchProjectDropdownsByUser());
      
      // First get the ticket ID from the ticket number
      dispatch(fetchTicketIdByNumber(ticket_number));
    } else {
      setIsInitialLoading(false);
    }
  }, [ticket_number, dispatch]);

  useEffect(() => {
    const ticketUuid = ticketIdByNumber?.ticket_id;
    if (ticketUuid) {
      setTicketId(ticketUuid);
      
      // Fetch all task data using the UUID
      const fetchAllData = async () => {
        try {
          await Promise.all([
            dispatch(fetchTicketDetails(ticketUuid)).unwrap(),
            dispatch(fetchTicketComments(ticketUuid)).unwrap(),
            dispatch(fetchWorklogSummary(ticketUuid)).unwrap(),
            dispatch(fetchTicketHistory(ticketUuid)).unwrap()
          ]);
        } catch (error) {
          console.error('Failed to fetch task data:', error);
        } finally {
          setIsInitialLoading(false);
        }
      };
      
      fetchAllData();
    } else if (!ticketIdByNumberLoading && ticketIdByNumber !== undefined) {
      // If ticketIdByNumber fetch is complete but no ticket_id found
      setIsInitialLoading(false);
    }
  }, [ticketIdByNumber, ticketIdByNumberLoading, dispatch]);

  useEffect(() => {
    if (ticketId && isFromEmail) {
      dispatch(markNotificationsRead({ ticketId, is_read: true }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  useEffect(() => {
    if (ticketDetails) {
      setSelectedTask(ticketDetails);
    }
  }, [ticketDetails]);

  const taskProjectId =
    selectedTask?.project?.id ||
    selectedTask?.project_id ||
    (typeof selectedTask?.project === 'object' ? selectedTask?.project?.id : selectedTask?.project) ||
    null;

  const errorMessage = typeof error === 'string' ? error : (error?.message || '');
  const isAccessDenied = Boolean(
    errorMessage &&
    (
      error?.status === 403 ||
      errorMessage.toLowerCase().includes('access denied') ||
      errorMessage.toLowerCase().includes('do not have access')
    )
  );

  const handleEdit = (task) => {
    setSelectedTask(task);
    setIsEditOpen(true);
  };

  const handleUpdateTask = async (taskData) => {
    try {
      if (!taskData.id) {
        return;
      }
      await dispatch(updateTicket({ ticketId: taskData.id, ticketData: taskData })).unwrap();
      // Refresh ticket details
      if (ticketId) {
        await dispatch(fetchTicketDetails(ticketId));
      }
      setIsEditOpen(false);
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  return (
    <div className="dashboard">
      <Navbar />
      <main className="main">
        {(ticketDetailsLoading || ticketIdByNumberLoading || isInitialLoading) ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            {t('taskViewPage.loadingTaskDetails')}
          </Box>
        ) : isAccessDenied ? (
          <Dialog
            open
            PaperProps={{
              sx: {
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 24px 80px rgba(15, 23, 42, 0.22)',
                maxWidth: 520,
                width: 'calc(100% - 32px)',
                border: `1px solid ${COLORS.border}`
              }
            }}
          >
            <DialogTitle sx={{ pb: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.text.primary }}>
                {t('taskViewPage.accessDeniedTitle', 'Access denied')}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 0, pb: 3 }}>
              <Box
                sx={{
                  p: 2.25,
                  borderRadius: 3,
                  border: `1px solid ${COLORS.ui.error8}`,
                  background: `linear-gradient(180deg, ${COLORS.ui.error8} 0%, rgba(255,255,255,0.96) 100%)`
                }}
              >
                <Typography variant="body1" sx={{ color: COLORS.text.primary, mb: 1, lineHeight: 1.6 }}>
                  {errorMessage || t('projects.prefixLoadFailed', 'You do not have access to this project')}
                </Typography>
              </Box>
            </DialogContent>
          </Dialog>
        ) : (error && !selectedTask) ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
            {errorMessage || t('taskViewPage.errorLoadingTask')}
          </Box>
        ) : selectedTask ? (
          <>
            <TaskViewModal
              open={true}
              onClose={() => {}}
              onEdit={handleEdit}
              taskData={selectedTask}
              focusedCommentId={focusedCommentId}
              onViewTask={(task) => {
                const ticketNumber = task?.ticket_number || ticketDetails?.ticket_number || ticket_number;
                if (ticketNumber) {
                  window.open(`/task/${encodeURIComponent(ticketNumber)}`, '_blank');
                }
              }}
              fullScreen
            />
          </>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            {t('taskViewPage.taskNotFound')}
          </Box>
        )}
        <EditTaskModal
          open={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          onSubmit={handleUpdateTask}
          taskData={selectedTask || {}}
          selectedProjectId={taskProjectId}
        />
      </main>
    </div>
  );
};

export default TaskViewPage;
